<template>
  <div style="position:relative" :style="'height:'+(wHeight-113)+'px;display:flex;flex-direction:column'">

    <!-- Sticky header: title + tabs -->
    <div style="position:sticky;top:0;z-index:5;background:#fff">
      <div class="d-flex align-center justify-center px-3 pt-3 pb-2">
        <v-icon color="black" class="mr-2">mdi-star-circle</v-icon>
        <span class="text-h6 font-weight-bold black--text">{{ $t(page+'.Reviews') }}</span>
      </div>

      <v-tabs v-model="tab" color="black" background-color="white" grow height="42" class="modern-tabs">
        <v-tabs-slider color="black"></v-tabs-slider>
        <v-tab v-for="(item, idx) in tabs" :key="idx" class="text-caption font-weight-bold">
          <v-progress-circular v-if="loading" indeterminate size="14" width="2" color="black" class="mr-1"></v-progress-circular>
          {{ item }}
        </v-tab>
      </v-tabs>
      <v-divider class="fw"></v-divider>
    </div>

    <!-- Scrollable content -->
    <div ref="scrollArea" class="overflow-y-auto flex-grow-1" @scroll="onScroll">

      <div v-if="loading" class="d-flex justify-center align-center py-10">
        <v-progress-circular indeterminate size="64" width="5" color="black"></v-progress-circular>
      </div>

      <v-tabs-items v-else v-model="tab" touchless>

        <v-tab-item v-for="(channel, idx) in tabChannels" :key="idx">
          <template v-if="channel === 'ibook'">
          <reviews-summary channel="ibook" :locationId="locationId" :active="tab === idx"></reviews-summary>
          <div v-if="!reviews || !reviews.length" class="empty-state">
            <v-icon size="48" color="grey lighten-1">mdi-comment-text-outline</v-icon>
            <div class="mt-2 grey--text">{{ $t(page+'.No reviews found') }}</div>
          </div>

          <div v-else class="pa-3">
            <div
              v-for="(r, i) in reviews"
              :key="i"
              class="review-card mb-3"
            >
              <div class="d-flex align-start pa-3 pb-2">
                <v-avatar size="40" :color="avatarColor(r.username)" class="mr-3 flex-shrink-0">
                  <span class="white--text text-body-2 font-weight-bold">{{ initials(r.username) }}</span>
                </v-avatar>

                <div class="flex-grow-1" style="min-width:0">
                  <div class="d-flex align-start justify-space-between">
                    <div style="min-width:0">
                      <div class="font-weight-bold text-body-2 text-truncate">{{ r.username }}</div>
                      <div class="caption grey--text text-truncate" v-if="r.userLocation!='N/D'">{{ r.userLocation }}</div>
                    </div>
                    <div class="caption grey--text text-right flex-shrink-0 ml-2">{{ r.travelDate }}</div>
                  </div>

                  <div class="d-flex align-center mt-1">
                    <v-icon v-for="n in 5" :key="n" size="16"
                      :color="n <= r.score ? 'amber' : 'grey lighten-2'">
                      {{ n <= r.score ? 'mdi-star' : 'mdi-star-outline' }}
                    </v-icon>
                  </div>
                </div>
              </div>

              <div class="sub-scores px-3 pb-3" v-if="r.score<5">
                <div class="sub-score-item">
                  <span class="sub-score-label">Location</span>
                  <span class="sub-score-stars">
                    <v-icon v-for="n in 5" :key="'l'+n" size="12"
                      :color="n <= r.location ? 'amber' : 'grey lighten-2'">
                      {{ n <= r.location ? 'mdi-star' : 'mdi-star-outline' }}
                    </v-icon>
                  </span>
                </div>
                <div class="sub-score-item">
                  <span class="sub-score-label">Food</span>
                  <span class="sub-score-stars">
                    <v-icon v-for="n in 5" :key="'f'+n" size="12"
                      :color="n <= r.food ? 'amber' : 'grey lighten-2'">
                      {{ n <= r.food ? 'mdi-star' : 'mdi-star-outline' }}
                    </v-icon>
                  </span>
                </div>
                <div class="sub-score-item">
                  <span class="sub-score-label">Service</span>
                  <span class="sub-score-stars">
                    <v-icon v-for="n in 5" :key="'s'+n" size="12"
                      :color="n <= r.service ? 'amber' : 'grey lighten-2'">
                      {{ n <= r.service ? 'mdi-star' : 'mdi-star-outline' }}
                    </v-icon>
                  </span>
                </div>
                <div class="sub-score-item">
                  <span class="sub-score-label">Q/P</span>
                  <span class="sub-score-stars">
                    <v-icon v-for="n in 5" :key="'v'+n" size="12"
                      :color="n <= r.value ? 'amber' : 'grey lighten-2'">
                      {{ n <= r.value ? 'mdi-star' : 'mdi-star-outline' }}
                    </v-icon>
                  </span>
                </div>
              </div>

              <v-divider v-if="r.review" class="mx-3 fw"></v-divider>

              <div v-if="r.review" class="px-3 py-3 text-body-2 grey--text text--darken-3 review-text">
                {{ r.review }}
              </div>
            </div>

            <!-- Pagination -->
            <div class="d-flex justify-center align-center pa-2 mt-1" v-if="reviews && reviews.length">
              <v-btn icon small class="pagination-btn" :disabled="currentPage[0] <= 1 || loadingPage" @click="changePage(currentPage[0] - 1)">
                <v-icon size="18">mdi-chevron-left</v-icon>
              </v-btn>
              <span class="caption grey--text mx-3">{{ $t(page+'.Page') }} {{ currentPage[0] }}</span>
              <v-btn icon small class="pagination-btn" :disabled="!hasNextPage[0] || loadingPage" @click="changePage(currentPage[0] + 1)">
                <v-icon size="18">mdi-chevron-right</v-icon>
              </v-btn>
              <v-progress-circular v-if="loadingPage" indeterminate size="16" width="2" color="black" class="ml-2"></v-progress-circular>
            </div>
          </div>
          </template>
          <template v-else-if="channel === 'tripadvisor'">
          <reviews-summary channel="tripadvisor" :locationId="locationId" :active="tab === idx"></reviews-summary>
          <div v-if="!reviewsTA || !reviewsTA.length" class="empty-state">
            <v-icon size="48" color="grey lighten-1">mdi-owl</v-icon>
            <div class="mt-2 grey--text">{{ $t(page+'.No TripAdvisor reviews found') }}</div>
          </div>

          <div v-else class="pa-3">
            <div v-for="(r, i) in reviewsTA" :key="i" class="review-card mb-3">
              <div class="d-flex align-start pa-3 pb-2">
                <v-avatar size="40" class="mr-3 flex-shrink-0" v-if="r.avatar">
                  <v-img :src="r.avatar"></v-img>
                </v-avatar>
                <v-avatar size="40" :color="avatarColor(r.username)" class="mr-3 flex-shrink-0" v-else>
                  <span class="white--text text-body-2 font-weight-bold">{{ initials(r.username) }}</span>
                </v-avatar>

                <div class="flex-grow-1" style="min-width:0">
                  <div class="d-flex align-start justify-space-between">
                    <div style="min-width:0">
                      <div class="font-weight-bold text-body-2 text-truncate">{{ r.username }}</div>
                      <div class="caption grey--text text-truncate" v-if="r.userLocation!='N/D'">{{ r.userLocation }}</div>
                    </div>
                    <div class="caption grey--text text-right flex-shrink-0 ml-2">{{ r.travelDate }}</div>
                  </div>

                  <div class="d-flex align-center mt-1">
                    <v-icon v-for="n in 5" :key="n" size="15"
                      :color="n <= r.rating ? 'amber' : 'grey lighten-2'">
                      {{ n <= r.rating ? 'mdi-circle' : 'mdi-circle-outline' }}
                    </v-icon>
                  </div>

                  <div v-if="r.title" class="text-body-2 font-weight-medium mt-1">{{ r.title }}</div>
                </div>
              </div>

              <v-divider v-if="r.review" class="mx-3 fw"></v-divider>
              <div v-if="r.review" class="px-3 py-3 text-body-2 grey--text text--darken-3 review-text">
                {{ r.review }}
              </div>
            </div>

            <div class="d-flex justify-center align-center pa-2 mt-1" v-if="reviewsTA && reviewsTA.length">
              <v-btn icon small class="pagination-btn" :disabled="currentPage[1] <= 1 || loadingPage" @click="changePage(currentPage[1] - 1)">
                <v-icon size="18">mdi-chevron-left</v-icon>
              </v-btn>
              <span class="caption grey--text mx-3">{{ $t(page+'.Page') }} {{ currentPage[1] }}</span>
              <v-btn icon small class="pagination-btn" :disabled="!hasNextPage[1] || loadingPage" @click="changePage(currentPage[1] + 1)">
                <v-icon size="18">mdi-chevron-right</v-icon>
              </v-btn>
              <v-progress-circular v-if="loadingPage" indeterminate size="16" width="2" color="black" class="ml-2"></v-progress-circular>
            </div>
          </div>
          </template>
          <template v-else-if="channel === 'google'">
          <reviews-summary channel="google" :locationId="locationId" :active="tab === idx"></reviews-summary>
          <div v-if="!reviewsG || !reviewsG.length" class="empty-state">
            <v-icon size="48" color="grey lighten-1">mdi-google</v-icon>
            <div class="mt-2 grey--text">{{ $t(page+'.No Google reviews found') }}</div>
          </div>

          <div v-else class="pa-3">
            <div v-for="(r, i) in reviewsG" :key="i" class="review-card mb-3">
              <div class="d-flex align-start pa-3 pb-2">
                <v-avatar size="40" :color="avatarColor(r.username)" class="mr-3 flex-shrink-0">
                  <span class="white--text text-body-2 font-weight-bold">{{ initials(r.username) }}</span>
                </v-avatar>

                <div class="flex-grow-1" style="min-width:0">
                  <div class="d-flex align-start justify-space-between">
                    <div style="min-width:0">
                      <div class="font-weight-bold text-body-2 text-truncate">{{ r.username }}</div>
                      <div class="caption grey--text text-truncate">{{ r.travelDate }}</div>
                    </div>
                    <v-chip x-small outlined color="black" class="font-weight-bold flex-shrink-0">
                      {{ r.rating }}
                    </v-chip>
                  </div>

                  <div class="d-flex align-center mt-1">
                    <v-icon v-for="n in 5" :key="n" size="15"
                      :color="n <= r.rating ? 'amber' : 'grey lighten-2'">
                      {{ n <= r.rating ? 'mdi-star' : 'mdi-star-outline' }}
                    </v-icon>
                  </div>

                  <div v-if="r.title" class="text-body-2 font-weight-medium mt-1">{{ r.title }}</div>
                </div>
              </div>
            </div>

            <div class="d-flex justify-center align-center pa-2 mt-1" v-if="reviewsG && reviewsG.length">
              <v-btn icon small class="pagination-btn" :disabled="currentPage[2] <= 1 || loadingPage" @click="changePage(currentPage[2] - 1)">
                <v-icon size="18">mdi-chevron-left</v-icon>
              </v-btn>
              <span class="caption grey--text mx-3">{{ $t(page+'.Page') }} {{ currentPage[2] }}</span>
              <v-btn icon small class="pagination-btn" :disabled="!hasNextPage[2] || loadingPage" @click="changePage(currentPage[2] + 1)">
                <v-icon size="18">mdi-chevron-right</v-icon>
              </v-btn>
              <v-progress-circular v-if="loadingPage" indeterminate size="16" width="2" color="black" class="ml-2"></v-progress-circular>
            </div>
          </div>
          </template>
        </v-tab-item>
      </v-tabs-items>
    </div>

    <!-- Go to top FAB -->
    <v-btn
      v-show="showGoTop"
      fab small dark color="black"
      style="position:absolute;bottom:16px;right:16px;z-index:10"
      @click="scrollToTop"
    >
      <v-icon>mdi-chevron-up</v-icon>
    </v-btn>

  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import ReviewsSummary from './ReviewsSummary.vue'

export default {
  name: 'ReviewsTab',

  components: { ReviewsSummary },

  props: {
    channel: { type: String, default: null },
    locationId: { type: String, default: null },
    isActive:   { type: Boolean, default: false },
  },

  data() {
    return {
      page: 'Booking',
      wHeight: window.innerHeight,
      loading: false,
      loadingPage: false,
      locationId:null,
      tab: 0,
      reveal: null,
      reviews:     null,
      reviewsTA:   null,
      reviewsG:    null,
      currentPage: { 0: 1, 1: 1, 2: 1 },
      hasNextPage: { 0: false, 1: false, 2: false },
      showGoTop:   false,
      scoreCols: [],
      reviewScoreCols: [
        { name: 'Cibo',     code: 'food'     },
        { name: 'Serv',     code: 'service'  },
        { name: 'Q/P',      code: 'value'    },
        { name: 'Location', code: 'location' },
      ],
      avatarPalette: ['#455A64', '#5D4037', '#00695C', '#4527A0', '#AD1457', '#37474F', '#6D4C41'],
    }
  },

  computed: {
    ...mapGetters(['settings']),

    tabs() {
      const t = []
      if (this.settings?.options?.showInternalReviewsInDirectBooking == '1') t.push('ibook')
      if (this.settings?.options?.showTripAdvisorReviewsInDirectBooking == '1') t.push('tripadvisor')
      if (this.settings?.options?.showGoogleReviewsInDirectBooking == '1') t.push('google')
      return t
    },

    tabChannels() {
      const c = []
      if (this.settings?.options?.showInternalReviewsInDirectBooking == '1') c.push('ibook')
      if (this.settings?.options?.showTripAdvisorReviewsInDirectBooking == '1') c.push('tripadvisor')
      if (this.settings?.options?.showGoogleReviewsInDirectBooking == '1') c.push('google')
      return c
    },
  },

  watch: {
    isActive(val) {
      if (val) this.getData()
    },
    tab(val) {
      this.$set(this.currentPage, val, 1)
      this.scrollToTop()
      const channel = this.tabChannels[val]
      const notLoaded = (channel === 'ibook'       && !this.reviews)   ||
                        (channel === 'tripadvisor'  && !this.reviewsTA) ||
                        (channel === 'google'       && !this.reviewsG)
      if (notLoaded) this.loadPage(1, val)
    },
  },

			 
									 
	

  mounted() {
    window.addEventListener('resize', this.onResize)
    // Load the first tab's data as soon as the component is on the page,
    // regardless of the isActive prop's timing — fixes the case where the
    // tab watcher never fires (e.g. only one channel enabled).
    this.getData()
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.onResize)
  },

  methods: {
    onResize() {
      this.wHeight = window.innerHeight
    },

    onScroll(e) {
      this.showGoTop = e.target.scrollTop > 120
    },

    scrollToTop() {
      if (this.$refs.scrollArea) {
        this.$refs.scrollArea.scrollTo({ top: 0, behavior: 'smooth' })
      }
    },

    initials(name) {
      if (!name) return '?'
      const parts = name.trim().split(/\s+/)
      const first = parts[0]?.[0] || ''
      const last = parts.length > 1 ? parts[parts.length - 1][0] : ''
      return (first + last).toUpperCase()
    },

    avatarColor(name) {
      if (!name) return this.avatarPalette[0]
      let hash = 0
      for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash)
      return this.avatarPalette[Math.abs(hash) % this.avatarPalette.length]
    },

    async getData() {
      if (this.loading || this.loadingPage) return
      const channel = this.tabChannels[this.tab]
      const alreadyLoaded = (channel === 'ibook'      && this.reviews)   ||
                            (channel === 'tripadvisor' && this.reviewsTA) ||
                            (channel === 'google'      && this.reviewsG)
      if (alreadyLoaded) return
      await this.loadPage(1, this.tab)
      this.locationId=this.settings?.options?.tripadvisorId 
    },

    async loadPage(page, tabIndex) {
      tabIndex = tabIndex ?? this.tab
      page === 1 ? this.loading = true : this.loadingPage = true
      try {
        const res = await this.$http.post('/review/review.php', {
          action: 'Reviews_directBooking_get',
          page: page,
          channel: this.tabChannels[tabIndex],
        })
        if (res.data.errorid === '0') {
          const data = res.data.payloads
          const channel = this.tabChannels[tabIndex]
          if (channel === 'ibook')       this.reviews   = data.reviews   || []
          if (channel === 'tripadvisor') this.reviewsTA = data.reviewsTA || []
          if (channel === 'google')      this.reviewsG  = data.reviewsG  || []
          this.$set(this.hasNextPage, tabIndex, !!data.hasNextPage)
          this.$set(this.currentPage, tabIndex, data.currentPage || page)
          this.scrollToTop()
        }
      } catch (e) {
        console.error('ReviewsTab loadPage error', e)
      } finally {
        this.loading = false
        this.loadingPage = false
      }
    },

    async changePage(page) {
      if (page < 1) return
      await this.loadPage(page, this.tab)
    },
  },
}
</script>

<style scoped>
hr.fw {
  min-width: 100%;
  margin:0!important;
} 

.flex-grow-1 { flex: 1 1 auto; }

.modern-tabs ::v-deep .v-tab {
  letter-spacing: 0.02em;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 64px 24px;
  text-align: center;
}

/* Bordered, shadow-free card — matches the outlined tile look */
.review-card {
  border: 1.5px solid #e0e0e0;
  border-radius: 16px;
  background: #fff;
}

.review-text {
  line-height: 1.5;
  white-space: pre-line;
}

.sub-scores {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4px 16px;
}

.sub-score-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.sub-score-label {
  font-size: 0.68rem;
  color: #757575;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

/* Pagination buttons: same bordered-pill treatment as the tiles */
.pagination-btn {
  border: 1.5px solid #e0e0e0 !important;
}

.pagination-btn.v-btn--disabled {
  opacity: 0.4;
}
</style>